import { useState, useEffect, useCallback, useRef } from 'react';
import { hfService } from '../../../services/hfService';

const useHFData = () => {
  const [datasets, setDatasets] = useState([]);
  const [activeDataset, setActiveDataset] = useState(null);
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [rfRate, setRfRate] = useState(0.025);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cache = useRef({});

  const loadDatasets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await hfService.getDatasets();
      const list = res.data.results || res.data;
      setDatasets(list);
      if (list.length > 0 && !activeDataset) {
        const ds = list[0];
        setActiveDataset(ds);
        const detail = await hfService.getDataset(ds.id);
        const fullDs = detail.data;
        setActiveDataset(fullDs);
        const fundNames = (fullDs.funds || []).map(f => f.display_name).slice(0, 10);
        setSelectedFunds(fundNames);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeDataset]);

  useEffect(() => {
    loadDatasets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear cache when params change
  const paramsKey = JSON.stringify({
    dataset_id: activeDataset?.id,
    funds: selectedFunds.slice().sort(),
    date_start: dateRange.start,
    date_end: dateRange.end,
    rf_rate: rfRate,
  });

  const prevParamsKey = useRef(paramsKey);
  useEffect(() => {
    if (prevParamsKey.current !== paramsKey) {
      cache.current = {};
      prevParamsKey.current = paramsKey;
    }
  }, [paramsKey]);

  const cachedFetch = useCallback(async (key, fetchFn) => {
    if (cache.current[key]) {
      return cache.current[key];
    }
    const result = await fetchFn();
    cache.current[key] = result;
    return result;
  }, []);

  const selectDataset = async (id) => {
    try {
      setLoading(true);
      cache.current = {};
      const res = await hfService.getDataset(id);
      setActiveDataset(res.data);
      const fundNames = (res.data.funds || []).map(f => f.display_name).slice(0, 10);
      setSelectedFunds(fundNames);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadDataset = async (file, name) => {
    const res = await hfService.uploadDataset(file, name);
    cache.current = {};
    await loadDatasets();
    return res.data;
  };

  const getAnalyticsParams = () => ({
    dataset_id: activeDataset?.id,
    funds: selectedFunds,
    date_start: dateRange.start || undefined,
    date_end: dateRange.end || undefined,
    rf_rate: rfRate,
  });

  return {
    datasets, activeDataset, selectedFunds, dateRange, rfRate, loading, error,
    setSelectedFunds, setDateRange, setRfRate, selectDataset, uploadDataset,
    getAnalyticsParams, loadDatasets, cachedFetch,
  };
};

export default useHFData;
